/**
 * NTN Verify Hub — shared design tokens for the applicant lifecycle.
 *
 * Official 6-stage workflow (Complete is the final stage). The numbered
 * stages are: Application Submitted, Screening & Reports, Verification &
 * Validation, Additional Information Needed, Decision-Ready Review. "Complete"
 * is the terminal outcome state (not counted as a numbered stage).
 *
 * Single source of truth for stage keys, labels, icons, and color tones used
 * across the public landing page AND the authenticated app (dashboards,
 * progress trackers, verification queue). Keep this in sync with the landing
 * page so logged-in users feel they entered the same product that was
 * advertised.
 */
import {
  Inbox,
  Briefcase,
  FileCheck,
  MessageSquare,
  ListChecks,
  CheckCircle2,
} from "lucide-react";

/* ----- Pipeline stages (matches LandingPage.jsx PIPELINE_STAGES) -----
 * NOTE: backend `verifyStatus` strings are intentionally kept unchanged from
 * their original semantics; only the user-visible `label` and `description`
 * are updated to the official NTN Verify Hub workflow language. This avoids
 * a destructive data migration on the persisted `verify_status` field. */
export const PIPELINE_STAGES = [
  { key: "submitted",   verifyStatus: "application_received",          label: "Application Submitted",      icon: Inbox,         tone: "neutral",
    description: "Application has been received and entered into the NTN Verify Hub workflow." },
  { key: "verify",      verifyStatus: "verification_in_progress",      label: "Screening & Reports",         icon: Briefcase,     tone: "active",
    description: "Background screening reports are being generated and collected (credit, criminal, eviction, identity, and other products)." },
  { key: "validation",  verifyStatus: "validation_in_progress",        label: "Verification & Validation",   icon: FileCheck,     tone: "active",
    description: "NTN is actively processing the file — employment, landlord, income review, document validation, and application consistency." },
  { key: "additional",  verifyStatus: "additional_information_requested", label: "Additional Information Needed", icon: MessageSquare, tone: "warning",
    description: "Temporarily waiting on information from an applicant, employer, landlord, property manager, or other source. Action required before processing can continue." },
  { key: "ready",       verifyStatus: "ready_for_processing",          label: "Decision-Ready Review",       icon: ListChecks,    tone: "ready",
    description: "All requested information received and all required services completed. File is in final NTN review and quality control before delivery." },
  { key: "complete",    verifyStatus: "complete",                      label: "Complete",                    icon: CheckCircle2,  tone: "complete",
    description: "All ordered services completed. File finalized and delivered." },
];

export const STAGE_KEYS = PIPELINE_STAGES.map((s) => s.key);

/* Reverse lookup: backend verify_status string -> stage object */
export const STAGE_BY_VERIFY_STATUS = Object.fromEntries(
  PIPELINE_STAGES.map((s) => [s.verifyStatus, s]),
);

/* ----- Tone palettes (matches LandingPage.jsx PIPELINE_DOT / PIPELINE_BAR) ----- */
export const PIPELINE_DOT = {
  neutral:  "bg-slate-300",
  active:   "bg-blue-500 ring-4 ring-blue-100",
  warning:  "bg-amber-500 ring-4 ring-amber-100",
  ready:    "bg-violet-500 ring-4 ring-violet-100",
  complete: "bg-emerald-500",
};

export const PIPELINE_BAR = {
  neutral:  "bg-slate-200",
  active:   "bg-blue-100",
  warning:  "bg-amber-100",
  ready:    "bg-violet-100",
  complete: "bg-emerald-100",
};

/* Solid color for fills inside bars (no ring) */
export const PIPELINE_FILL = {
  neutral:  "bg-slate-400",
  active:   "bg-blue-500",
  warning:  "bg-amber-500",
  ready:    "bg-violet-500",
  complete: "bg-emerald-500",
};

/* Soft pill tones used for status chips */
export const PIPELINE_PILL = {
  neutral:  "bg-slate-100 text-slate-700",
  active:   "bg-blue-50 text-blue-700",
  warning:  "bg-amber-50 text-amber-700",
  ready:    "bg-violet-50 text-violet-700",
  complete: "bg-emerald-50 text-emerald-700",
};

/* Get the stage for a screening doc. v1.0.7 — prefers the server-computed
 * canonical `workflow_stage`, then `verify_status` (also computed server-side
 * on every list/detail payload); persisted scalars are never authoritative. */
export function stageFor(screening) {
  if (!screening) return PIPELINE_STAGES[0];
  const vs = screening.workflow_stage || screening.verify_status;
  if (vs && STAGE_BY_VERIFY_STATUS[vs]) return STAGE_BY_VERIFY_STATUS[vs];
  if (screening.overall_status === "complete") return PIPELINE_STAGES[5];
  return PIPELINE_STAGES[0];
}

/* Stage index (0..5) for a screening — used for ordering comparisons. */
export function stageIndexFor(screening) {
  const s = stageFor(screening);
  return PIPELINE_STAGES.findIndex((p) => p.key === s.key);
}

/* Household / group stage — the slowest member determines the household stage
 * (so the family isn't marked "Ready For Processing" until everyone is). When
 * every member is `complete`, the household is `complete`. */
export function householdStageFor(group) {
  // v1.0.8 — removed household members never gate the active household stage.
  const members = (group?.member_screenings || group?.members || []).filter((m) => !m?.removed);
  if (members.length === 0) return PIPELINE_STAGES[0];
  let minIdx = 5;
  for (const m of members) {
    const idx = stageIndexFor(m);
    if (idx < minIdx) minIdx = idx;
    if (minIdx === 0) break;
  }
  return PIPELINE_STAGES[minIdx];
}

/* Display label for a backend verify_status string. */
export function verifyStatusLabel(vs) {
  const stage = STAGE_BY_VERIFY_STATUS[vs];
  return stage ? stage.label : (vs || "").replace(/_/g, " ");
}

/* Canonical note-visibility predicate — the notes API returns
 * "property_manager" | "staff_only"; legacy docs may still say "shared". */
export function isPmVisibleNote(n) {
  return n?.visibility === "property_manager" || n?.visibility === "shared";
}

/* ----- v1.0.9 — FICO / DP score display states ----- */
export const FICO_INSUFFICIENT = -9999;

export function usableFico(dp) {
  if (!dp) return null;
  if (dp.fico_status === "insufficient_history" || dp.fico_status === "not_available") return null;
  const f = dp.fico_score;
  return typeof f === "number" && f !== FICO_INSUFFICIENT ? f : null;
}

export function ficoDisplay(fico, status) {
  if (status === "not_available") return "N/A";
  if (status === "insufficient_history" || fico === FICO_INSUFFICIENT) return "Insufficient Credit History";
  return fico != null ? String(fico) : null;
}
